import { Constants } from '@areapif/areapif-backend'
import type { Database } from '@areapif/areapif-shared'
import type {
  Evidence,
  InspectionLevel,
  InspectionLocation,
  InspectionStatusId,
  InspectionType,
  PhotoCategory,
  SealCategory,
  SummaryViewStep,
} from '@areapif/areapif-shared'
import {
  createInspectionDetails,
  isTerminalInspectionType,
  PHOTO_CATEGORIES,
  photoToEvidence,
  sealToEvidence,
} from '@areapif/areapif-shared'
import { z } from 'zod'

import type { EvidenceStepView } from '../domain/activeInspection'
import { buildEvidenceSteps, deriveInspectionState, deriveTerminalInspectionState, InspectionState } from '../domain/activeInspection'

export type InspectionService = Database['public']['Enums']['service']

/**
 * PostgREST columns requested for the `inspections` resource. Evidence lives in
 * the `inspection_photos` / `inspection_seals` child tables (unlike
 * `historical_inspections`, which denormalises them into composite arrays), so
 * both are embedded in the same request.
 */
export const ACTIVE_INSPECTION_COLUMNS = [
  '*',
  'inspection_photos(id,category,server_created_at)',
  'inspection_seals(id,category,serial_id,ocr_payload,server_created_at)',
] as const

const photoRowSchema = z.object({
  id: z.string(),
  category: z.string(),
  server_created_at: z.string().optional(),
})

const sealRowSchema = z.object({
  id: z.string(),
  // Nullable since seal_processing_jobs was merged into inspection_seals: a seal
  // is inserted as a pending job and only gets a category once analysis
  // completes. The generated @areapif/areapif-backend types still declare this
  // column NOT NULL — they have not been regenerated since that migration — so
  // the database, not the types, is what this schema follows.
  category: z.string().nullable().optional(),
  serial_id: z.string().nullable().optional(),
  ocr_payload: z.string().nullable().optional(),
  server_created_at: z.string().optional(),
})

export const activeInspectionRowSchema = z.object({
  id: z.string(),
  container: z.string(),
  // NULL on a manual inspection until SOSTRAT first matches it and assigns the
  // date: the row is still an inspection, shown undated rather than skipped.
  day: z.string().nullable(),
  dock: z.number().nullable().optional(),
  inspection_level: z.string(),
  is_local: z.boolean().optional(),
  location: z.string(),
  services: z.array(z.string()).optional(),
  // Validated against the database enum rather than accepted as any string: a
  // value the app has not heard of is drift, and the row is skipped with that
  // reason on screen instead of being cast into a status it is not.
  status: z.enum(Constants.public.Enums.status),
  /**
   * The STI discriminator: which lifecycle vocabulary `status` belongs to.
   * Not `location` — the CHECK constraint keys the vocabularies on this column,
   * and it keeps its meaning if a location is ever reclassified.
   */
  type: z.enum(Constants.public.Enums.inspection_type),
  /** SOSTRAT feed provenance, read by the shared terminal derivation. */
  seen_in_inspections_sostrat: z.boolean(),
  server_created_at: z.string().optional(),
  server_updated_at: z.string().optional(),
  inspection_photos: z.array(photoRowSchema).optional(),
  inspection_seals: z.array(sealRowSchema).optional(),
})

export type ActiveInspectionRow = z.infer<typeof activeInspectionRowSchema>

/**
 * What deleting an inspection would destroy, counted off the child evidence
 * tables. Kept separate from `evidenceSteps`, which maps evidence onto flow
 * steps and is therefore the wrong thing to count: it drops seals still awaiting
 * analysis, and it is emptied outright for a cancelled inspection.
 */
export interface InspectionEvidenceCounts {
  /** Every photo on the inspection, including the container/license one. */
  photos: number
  /** Every seal, classified or still being processed. */
  seals: number
  /**
   * Whether any of it is inspection work rather than the container photo a
   * manual inspection is born with. Drives whether the delete confirmation
   * spells out what will be lost: a just-created inspection would otherwise
   * trigger a "1 photo" warning on every delete.
   */
  hasCapturedEvidence: boolean
}

/**
 * A seal whose analysis has not completed. Its file is uploaded and its row
 * exists, but without a category it sits on no step of the flow, so the step
 * strip and the galleries cannot place it.
 */
export interface UnclassifiedSeal {
  /** Storage key of the seal image, which is also the row id. */
  id: string
  serialId: string | null
}

/**
 * A row of the backoffice inspections table: the raw record plus everything
 * derived from its evidence.
 */
export interface ActiveInspection {
  id: string
  container: string
  /** NULL on a manual inspection SOSTRAT has not matched yet. */
  day: string | null
  location: InspectionLocation
  inspectionLevel: InspectionLevel
  services: InspectionService[]
  /** Raw SOSTRAT status, kept so it can be filtered on directly. */
  statusId: InspectionStatusId
  /** Which lifecycle the row follows: the PIF ladder, or the terminal one. */
  type: InspectionType
  /** Coarse state shown in the table (PLANNED / IN_PROGRESS / FINISHED / CANCELLED). */
  state: InspectionState
  /**
   * `is_local`: the inspection was created by hand in the app, not by SOSTRAT.
   * The origin never changes, and it is what gates the hard delete.
   */
  isLocal: boolean
  /**
   * `seen_in_inspections_sostrat`: SOSTRAT's inspections feed has returned this
   * inspection at least once. Latched by the sync's first sighting and never
   * cleared, so it only ever goes false -> true. On a row the app created it is
   * what turns the hand marker green.
   */
  seenInSostrat: boolean
  evidenceSteps: EvidenceStepView[]
  /** What a delete would take with it, for the delete confirmation copy. */
  evidenceCounts: InspectionEvidenceCounts
  summaryView: SummaryViewStep[]
  /**
   * Storage keys of every photo and every seal file the inspection owns,
   * unclassified seals included: what "Descargar todo" zips. The child row id
   * is the object name in the `inspections` bucket, so the key is the id.
   */
  evidenceFiles: string[]
  /** Seals still awaiting classification: downloadable, but placed on no step. */
  unclassifiedSeals: UnclassifiedSeal[]
  /**
   * Id of the archived copy in `historical_inspections`. The archival trigger
   * reuses the inspection id verbatim, so it only exists once the inspection is
   * FINALIZADO.
   */
  historicalInspectionId: string | null
}

/** `server_created_at` is an ISO timestamp; the shared library wants epoch millis. */
const toTimestamp = (createdAt: string | undefined): number => {
  if (createdAt === undefined) {
    return Date.now()
  }

  const parsed = new Date(createdAt).getTime()
  return Number.isNaN(parsed) ? Date.now() : parsed
}

type SealRow = NonNullable<ActiveInspectionRow['inspection_seals']>[number]

/**
 * Whether analysis has given the seal a category. The category is what places a
 * seal on a step of the flow; until it arrives the seal is a file and a row,
 * and nothing more.
 */
const isClassified = (seal: SealRow): boolean => seal.category != null && seal.category !== ''

const toEvidences = (row: ActiveInspectionRow): Evidence[] => {
  const photos = (row.inspection_photos ?? []).map(photo =>
    photoToEvidence({
      id: photo.id,
      category: photo.category as PhotoCategory,
      createdAt: toTimestamp(photo.server_created_at),
    }),
  )

  // A seal still being processed has no category yet, and the category is what
  // places a seal on a step of the flow. Those seals are counted (the file is
  // uploaded) but left out of the step mapping until they are classified.
  const seals = (row.inspection_seals ?? []).filter(isClassified).map(seal =>
    sealToEvidence({
      id: seal.id,
      category: seal.category as SealCategory,
      createdAt: toTimestamp(seal.server_created_at),
      serialId: seal.serial_id ?? '',
      requestMetadata: {},
    }),
  )

  return [...photos, ...seals]
}

/**
 * Count what a delete would destroy, straight off the raw embedded arrays.
 *
 * Deliberately not derived from `toEvidences`: a seal whose analysis has not
 * completed yet carries no category and is filtered out there, but its file is
 * already uploaded and the cascade would delete it all the same. Undercounting
 * here would understate the loss in the confirmation dialog.
 */
const countEvidence = (row: ActiveInspectionRow): InspectionEvidenceCounts => {
  const photos = row.inspection_photos ?? []
  const seals = row.inspection_seals ?? []
  const capturedPhotos = photos.filter(photo => photo.category !== PHOTO_CATEGORIES.CONTAINER).length

  return {
    photos: photos.length,
    seals: seals.length,
    hasCapturedEvidence: capturedPhotos > 0 || seals.length > 0,
  }
}

/**
 * Convert a raw `inspections` row (with embedded photos and seals) into the
 * table row, deriving state, completeness and the evidence step strip through
 * the shared library's flow model.
 */
export const convertToActiveInspection = (row: ActiveInspectionRow): ActiveInspection => {
  const statusId = row.status
  const evidences = toEvidences(row)

  const details = createInspectionDetails(
    row.id,
    row.container,
    row.inspection_level as InspectionLevel,
    statusId,
    evidences,
    row.dock ?? undefined,
    // 5.x branches the flow on location: an RP inspection at a terminal gets
    // CLOSED_RP_TERMINAL (5 steps) rather than CLOSED_RP (3). Omitting this
    // would silently keep the old, shorter flow.
    row.location as InspectionLocation,
  )

  const summaryView = details.summaryView

  // The two lifecycles are told apart by `type`, never by `location`. A terminal
  // row takes its state from the shared domain — the local PIF collapse has no
  // opinion to offer on a status that is not on the PIF ladder.
  const state = isTerminalInspectionType(row.type)
    ? deriveTerminalInspectionState({
        statusId,
        seenInInspectionsSostrat: row.seen_in_inspections_sostrat,
        hasEvidence: evidences.length > 0,
      })
    : deriveInspectionState(statusId, evidences.length)

  return {
    id: row.id,
    container: row.container,
    day: row.day,
    location: row.location as InspectionLocation,
    inspectionLevel: row.inspection_level as InspectionLevel,
    services: (row.services ?? []) as InspectionService[],
    statusId,
    type: row.type,
    state,
    isLocal: row.is_local ?? false,
    seenInSostrat: row.seen_in_inspections_sostrat,
    // A cancelled inspection was called off before the flow applied, so the
    // steps it "missed" are not missing — showing the strip would flag red
    // gaps for work that was never going to happen.
    evidenceSteps: state === InspectionState.CANCELLED ? [] : buildEvidenceSteps(summaryView, state),
    // Counted even for a cancelled inspection, whose step strip is emptied
    // above: the rows and their files exist regardless of the lifecycle, and a
    // delete destroys them either way.
    evidenceCounts: countEvidence(row),
    summaryView,
    // Every file, straight off the raw arrays for the same reason as the counts:
    // an unclassified seal is on no step, but its file is in the bucket.
    evidenceFiles: [...(row.inspection_photos ?? []).map(photo => photo.id), ...(row.inspection_seals ?? []).map(seal => seal.id)],
    unclassifiedSeals: (row.inspection_seals ?? [])
      .filter(seal => !isClassified(seal))
      .map(seal => ({ id: seal.id, serialId: seal.serial_id ?? null })),
    historicalInspectionId: state === InspectionState.FINISHED ? row.id : null,
  }
}
